import {
  Button,
  Checkbox,
  IconButton,
  IconNavArrowLeft,
  IconPlus,
  IconTrash,
  Input,
  Table,
} from '@ftdr/blueprint-components-react';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { camelCase, isEmpty } from 'lodash';
import { validateEmailInput } from './input/EmailInput';
import { classNames, isMobileView } from '@utils';
import { Contract } from '@apis/models';
import { ContractModelVariant } from '@app/models/contract.model';
import RecipientsGenerator, {
  ContractRecipientData,
  RecipientType,
} from '@app/generators/recipientGenerator';
import { addressToString } from '@services/helpers';
import { dateTypes } from '@constants/dates';
import REText from '@components/wrappedBDS/REText';
import { getCurrentThemeConfig } from '@app/core/theme-config';

export enum Type {
  Invoice = 'invoice',
  Confirmation = 'confirmation',
  Renewal = 'renewal',
}

export interface Recipient {
  name: string;
  type: RecipientType;
  email: string;
  selectedTypes: Type[];
}

export interface RecipientRow extends Recipient {
  saved: boolean;
  onChange: (recipients: Recipient[]) => void; // needed to pass down SendDocuments's props.onChange callback
  confirmationSelected?: { selected: boolean };
  invoiceSelected?: { selected: boolean };
}

export interface Props {
  recipients: Recipient[];
  allowedTypes: Type[];
  onChange: (recipients: Recipient[]) => void;
  onSave?: () => void;
  onCancel?: () => void;
  disabled?: boolean;
  newOrderData?: any;
  onBackToSearch?: any;
  hideHeading?: boolean;
  displaySelectRecipientMessage?: boolean;
  onUpdateFormValidState?: (isValid: boolean) => void;
  newOrderClosingDate?: string;
  isNewOrder?: boolean;
}

interface EmailInputProps {
  email: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errors: string;
  isShort: boolean;
}

interface EmailCellProps {
  value: string;
  row: any; // TODO: type
  data: RecipientRow[];
}

interface NameInputProps {
  name: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errors: string;
}

interface NameCellProps {
  value: string;
  row: any; // TODO: type
  data: RecipientRow[];
}

export enum RecipientContext {
  SendDocuments = 1,
  SendRenewal,
}

const getRecipients = (recipientRows: RecipientRow[]): Recipient[] => {
  return recipientRows.map((recipient) => {
    const { name, email, type, selectedTypes } = recipient;

    return {
      name,
      email,
      type,
      selectedTypes,
    };
  });
};

const EmailInput: React.FC<EmailInputProps> = (props) => {
  return (
    <Input
      formField
      label=""
      size="medium"
      type="email"
      inputClassName={`sm-max:pt-0 text-base pr-2 send-documents-input ${props.isShort ? 'send-documents-input-short' : ''}`}
      placeholder="Email Address"
      value={props.email}
      error={props.errors}
      onChange={props.onChange}
    />
  );
};

const EmailCell: React.FC<EmailCellProps> = (props) => {
  const {
    row: { id, original },
    data,
    value: email,
  } = props;
  const { saved, onChange } = original;
  const dataCopy = [...data]; // copy to avoid mutating props
  const [errors, setErrors] = useState('');

  const isMobile = isMobileView();

  // Check if email is required based on selected checkboxes
  const isRequired = data[id].confirmationSelected?.selected || data[id].invoiceSelected?.selected;

  // Validate when checkbox state or email changes
  useEffect(() => {
    const errs = validateEmailInput(email, isRequired);
    setErrors(errs || '');
  }, [isRequired, email]);

  if (!isEmpty(email) && saved) return <REText variant="caption">{email}</REText>;

  const updateEmail = (newEmail: string): RecipientRow[] => {
    dataCopy[id].email = newEmail;

    return dataCopy;
  };

  const getOtherCount = () => {
    return data.filter(({ type }) => type === RecipientType.Other).length;
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(getRecipients(updateEmail(e.target.value)));
  };

  return (
    <>
      <div className="flex">
        <EmailInput
          email={email}
          onChange={onInputChange}
          errors={errors}
          isShort={isMobile && getOtherCount() > 1}
        />
        {getOtherCount() > 1 && (
          <IconButton
            label=""
            icon={<IconTrash />}
            variant="ghost"
            size="medium"
            color={getCurrentThemeConfig().sendDocumentsTrashIconColor}
            onClick={() => {
              dataCopy.splice(id, 1);

              onChange(getRecipients(dataCopy));
            }}
          />
        )}
      </div>
    </>
  );
};

const NameInput: React.FC<NameInputProps> = (props) => {
  return (
    <Input
      formField
      label=""
      size="medium"
      type="text"
      inputClassName="sm-max:pt-0 text-base pr-2 send-documents-input"
      placeholder="Recipient Name"
      value={props.name}
      error={props.errors}
      onChange={props.onChange}
    />
  );
};

const NameCell: React.FC<NameCellProps> = (props) => {
  const {
    row: { id, original },
    data,
    value: name,
  } = props;
  const { saved, onChange } = original;
  const dataCopy = [...data]; // copy to avoid mutating props
  const [errors, setErrors] = useState('');

  // Check if name is required based on selected checkboxes
  const isRequired = data[id].confirmationSelected?.selected || data[id].invoiceSelected?.selected;

  // Validate when checkbox state or name changes
  useEffect(() => {
    if (isRequired && isEmpty(name)) {
      setErrors('Name is required');
    } else {
      setErrors('');
    }
  }, [isRequired, name]);

  if (!isEmpty(name) && saved) return <REText variant="caption">{name}</REText>;

  const updateName = (newName: string): RecipientRow[] => {
    dataCopy[id].name = newName;

    return dataCopy;
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(getRecipients(updateName(e.target.value)));
  };

  return (
    <>
      <div className="flex">
        <NameInput name={name} onChange={onInputChange} errors={errors} />
      </div>
    </>
  );
};

const TypeCell: React.FC<{ value: string }> = ({ value }) => {
  return <REText variant="caption">{value}</REText>;
};

const addInvoiceToSelectedTypes = (recipientList: Recipient[]): Recipient[] => {
  return recipientList.map(recipient => {
    if (recipient.selectedTypes.length === 0) {
      return recipient;
    }
    return {
      ...recipient,
      selectedTypes: [...recipient.selectedTypes, Type.Invoice]
    };
  });
};

const removeInvoiceFromSelectedTypes = (recipientList: Recipient[]): Recipient[] => {
  return recipientList.map(recipient => ({
    ...recipient,
    selectedTypes: recipient.selectedTypes.filter(type => type !== Type.Invoice)
  }));
};

export const GetLatestRecipients = (
  contract: Contract | ContractRecipientData | any,
  type: ContractModelVariant,
  recipients: Recipient[],
  email: string,
  fullName: string,
  context: RecipientContext = RecipientContext.SendDocuments,
): Recipient[] => {
  // TODO: Normalize domain
  const generator = new RecipientsGenerator(recipients, email, fullName);
  let recipientList: Recipient[] = [];
  switch (type) {
    case ContractModelVariant.API:
      recipientList = generator.fromAPIContract(contract);
      break;
    case ContractModelVariant.App:
      recipientList = generator.fromAppContract(contract);
      break;
    case ContractModelVariant.NewOrderContextData:
      recipientList = generator.fromNewOrderContext(contract);
      break;
    default:
      return [];
  }

  if (context === RecipientContext.SendRenewal) {
    recipientList = recipientList.filter((r) => {
      return [
        RecipientType.Buyer,
        RecipientType.CoBuyer,
        RecipientType.InitiatingAgent,
        RecipientType.Myself,
        RecipientType.Other,
      ].includes(r.type);
    });
    recipientList.forEach((r) => {
      r.selectedTypes = [RecipientType.Buyer, RecipientType.CoBuyer].includes(r.type)
        ? [Type.Renewal]
        : [];
    });
  }

  if (contract.features.buyerPay) {
    if (contract.features.buyerPay.isBuyerPay) {
      recipientList = removeInvoiceFromSelectedTypes(recipientList)
    } else {
      recipientList = addInvoiceToSelectedTypes(recipientList)
    }
  }

  return recipientList;
};

const SendDocuments: React.FC<Props> = (props) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Calculate if form is valid by checking each recipient
  const isFormValid = (): boolean => {
    return props.recipients.every((recipient) => {
      const hasSelection = recipient.selectedTypes.length > 0;

      if (hasSelection) {
        const nameValid = !isEmpty(recipient.name);
        const emailError = validateEmailInput(recipient.email, true);
        return nameValid && isEmpty(emailError);
      }

      if (!isEmpty(recipient.email)) {
        const emailError = validateEmailInput(recipient.email, false);
        return isEmpty(emailError);
      }

      return true;
    });
  };

  const getData = (): RecipientRow[] => {
    const recipients = props.recipients.map((recipient) => {
      const data = {
        saved: recipient.type !== RecipientType.Other, // used to know whether or not to keep input field available - just needed for others atm
        onChange: props.onChange,
        ...recipient,
      };

      Object.keys(Type).forEach((type) => {
        data[`${camelCase(type)}Selected`] = { selected: data.selectedTypes.includes(Type[type]) };
      });

      return data;
    });

    //Place RecipientType.Other items at the bottom of the list
    const sortedRecipients = [
      ...recipients.filter((recipient) => recipient.type !== RecipientType.Other),
      ...recipients.filter((recipient) => recipient.type === RecipientType.Other),
    ];

    return sortedRecipients;
  };

  const updateSelection = (id: string, selected: boolean, type: Type): RecipientRow[] => {
    const data = getData();
    if (selected) data[id].selectedTypes = [...data[id].selectedTypes, type];

    if (!selected)
      data[id].selectedTypes = data[id].selectedTypes.filter(
        (selectedType) => type !== selectedType,
      );
    return data;
  };

  const getCheckbox = (row, type: Type) => {
    return (
      <div className={getCurrentThemeConfig().sendDocumentsCheckboxContainer}>
        <Checkbox
          label=""
          name=""
          hideLabel={true}
          onChange={({ target }) =>
            props.onChange(getRecipients(updateSelection(row.id, target.checked, type)))
          }
          checked={isInvoiceAllowed(type) ? row.original.selectedTypes.includes(type) : false}
          disabled={!isInvoiceAllowed(type)}
        />
      </div>
    );
  };

  // shouldDisableCheckBox returns true if the type is invoice and closing date does not exist
 const isInvoiceAllowed = (type: Type): boolean => {
  if (type !== Type.Invoice) return true;
  const doesHaveClosingDate: boolean =
    !!props.newOrderData?.detail?.dates?.find(
      (date) => date?.type === dateTypes.ESTCOE
    ) || !!props.newOrderData?.detail?.importantDates?.projectedClosingDate;
  return doesHaveClosingDate || !!props.newOrderClosingDate;
};

  const filterAllowedTypes = (columns) => {
    const test = columns.filter((x) => {
      if (!Object.values(Type).includes(x.accessor)) return true;

      if (Object.values(Type).includes(x.accessor) && !props.allowedTypes.includes(x.accessor))
        return false;

      return true;
    });

    return test;
  };

  const getColumns = () => {
    return [
      {
        Header: (
          <REText
            id="table-header-orderConfirmation"
            variant="heading-06"
            className={`${getCurrentThemeConfig().sendDocumentsHeaderConfirm} lg:text-center`}
          >
            Order <br /> Confirmation
          </REText>
        ),
        accessor: Type.Confirmation, // unused but string needed
        Cell: ({ row }) => getCheckbox(row, Type.Confirmation),
      },
      {
        Header: (
          <REText
            id="table-header-invoice"
            variant="heading-06"
            className={`${getCurrentThemeConfig().sendDocumentsHeaderInvoice} lg:text-center`}
          >
            Invoice
          </REText>
        ),
        accessor: Type.Invoice, // unused but string needed
        Cell: ({ row }) => getCheckbox(row, Type.Invoice),
      },
      {
        Header: (
          <REText
            id="table-header-recipients"
            variant="heading-06"
            className={getCurrentThemeConfig().sendDocumentsHeaderRecipients}
          >
            Recipients
          </REText>
        ),
        accessor: 'type',
        Cell: TypeCell,
      },
      {
        Header: (
          <REText
            id="table-header-recipientName"
            variant="heading-06"
            className={getCurrentThemeConfig().sendDocumentsHeaderName}
          >
            Recipient Name
          </REText>
        ),
        accessor: 'name',
        Cell: NameCell,
      },
      {
        Header: (
          <REText
            id="table-header-email"
            variant="heading-06"
            className={getCurrentThemeConfig().sendDocumentsEmail}
          >
            Email Address
          </REText>
        ),
        accessor: 'email',
        Cell: EmailCell,
      },
    ];
  };

  const getAddRecipientButton = (): React.ReactElement => {
    // leverage css
    return (
      <>
        <Button
          label="Add Recipient"
          startIcon={<IconPlus />}
          onClick={() =>
            props.onChange([
              ...props.recipients,
              { name: '', type: RecipientType.Other, email: '', selectedTypes: [] },
            ])
          }
          width="full"
          size="small"
          variant="outlined"
          className="md:hidden"
          color={getCurrentThemeConfig().sendDocumentsAddRecButtonColor}
          shape={getCurrentThemeConfig().sendDocumentsButtonShape}
        />
        <Button
          label="Add Recipient"
          startIcon={<IconPlus />}
          onClick={() =>
            props.onChange([
              ...props.recipients,
              { name: '', type: RecipientType.Other, email: '', selectedTypes: [] },
            ])
          }
          size="medium"
          variant="outlined"
          color={getCurrentThemeConfig().sendDocumentsAddRecButtonColor}
          shape={getCurrentThemeConfig().sendDocumentsButtonShape}
          className="hidden md:inline-flex"
        />
      </>
    );
  };

  const isSaveDisabled = (): boolean => {
    const hasAnySelection = props.recipients.some((x) => x.selectedTypes.length > 0);

    return props.disabled || !isFormValid() || !hasAnySelection || isSaving;
  };

  useEffect(() => {
    if (!props.onUpdateFormValidState) return;
    props.onUpdateFormValidState(isFormValid());
  }, [props.recipients]);

// Clears invoice selection when no COE date to prevent unintended delivery
  useEffect(() => {
  const invoiceAllowed = isInvoiceAllowed(Type.Invoice);
  if (!invoiceAllowed) {
    const hasInvoiceInSelectedTypes = props.recipients.some((r) =>
      r.selectedTypes.includes(Type.Invoice)
    );
    if (hasInvoiceInSelectedTypes) {
      props.onChange(
        props.recipients.map((r) => ({
          ...r,
          selectedTypes: r.selectedTypes.filter((t) => t !== Type.Invoice),
        }))
      );
    }
  }
}, [props.newOrderData, props.newOrderClosingDate]);

  const onSave = () => {
    setIsSaving(true);
    props.onSave();
  };

  return (
    <>
      <div className={getCurrentThemeConfig().sendDocumentsLabelContainer}>
        {!props.hideHeading && (
          <REText
            id="lbl-send-documents"
            variant={
              props.isNewOrder ? 'heading-05' : getCurrentThemeConfig().sendDocumentsLabelVariant
            }
            className={getCurrentThemeConfig().sendDocumentsLabel}
          >
            Send Documents
          </REText>
        )}

        {props.newOrderData && (
          <REText
            id="lbl-address"
            variant={getCurrentThemeConfig().sendDocumentsAddressVariant}
            className={getCurrentThemeConfig().sendDocumentsAddress}
          >
            {addressToString(
              props.newOrderData.summary.address.address1,
              props.newOrderData.summary.address.address2,
              props.newOrderData.summary.address.city,
              props.newOrderData.summary.address.state,
              props.newOrderData.summary.address.zip,
            )}
          </REText>
        )}
        {props.onBackToSearch && (
          <Button
            className="mt-4"
            label="Back to search results"
            startIcon={<IconNavArrowLeft />}
            onClick={props.onBackToSearch}
            size="small"
            variant="ghost"
          />
        )}
        {props.displaySelectRecipientMessage && (
          <REText id="lbl-error" variant="body-short" color="error" className="pb-4 pt-2 pl-1">
            Please select recipients to send the order documents. At least one recipient selection
            is required.
          </REText>
        )}
        <Table
          id="send-documents-table"
          data={getData()}
          columns={filterAllowedTypes(getColumns())}
          striped="gray"
          variant={getCurrentThemeConfig().sendDocumentsTableVariant}
          paginate={false}
          sortable={false}
        />
        <div className="md:flex">
          <div className={getCurrentThemeConfig().sendDocumentsContainerButton}>
            {getAddRecipientButton()}
          </div>
          <div
            className={classNames([
              'text-center md:flex-wrap md:flex-row-reverse md:justify-start md:items-center w-full md:pl-8 py-4',
              props.onSave ? 'md:flex ' : 'hidden',
            ])}
          >
            <div>
              <Button
                id="btn-send-notification"
                className="justify-center"
                label="Save & Continue"
                size="medium"
                onClick={onSave}
                disabled={isSaveDisabled()}
                width="full"
                labelAlign="center"
                shape={getCurrentThemeConfig().sendDocumentsButtonShape}
                color={getCurrentThemeConfig().sendDocumentsSaveButtonColor}
              />
            </div>
            <Button
              className="sm-max:mt-4 md:mr-4"
              label="Cancel"
              size="small"
              variant="ghost"
              shape={getCurrentThemeConfig().sendDocumentsButtonShape}
              onClick={props.onCancel}
            />
          </div>
        </div>
      </div>
    </>
  );
};

SendDocuments.defaultProps = {
  disabled: false,
};

export default SendDocuments;
